import React from "react";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import SeoMeta from "./seoMeta";
import PublicShareActions from "./PublicShareActions";

type FeatureAction = {
  label: string;
  href?: string;
  onClick?: () => void;
  variant?: "contained" | "outlined" | "text";
};

type PublicFeatureLandingPageProps = {
  path: string;
  seoTitle: string;
  seoDescription: string;
  eyebrow?: string;
  title: string;
  description: string;
  highlights?: string[];
  actions?: FeatureAction[];
  buildShareMessage: (url: string) => string;
  shareDescription?: string;
  children?: React.ReactNode;
};

function buildAbsoluteUrl(path: string) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  if (typeof window === "undefined") return `https://plany365.com${normalizedPath}`;
  return new URL(normalizedPath, window.location.origin).toString();
}

export default function PublicFeatureLandingPage({
  path,
  seoTitle,
  seoDescription,
  eyebrow,
  title,
  description,
  highlights = [],
  actions = [],
  buildShareMessage,
  shareDescription,
  children,
}: PublicFeatureLandingPageProps) {
  const shareUrl = React.useMemo(() => buildAbsoluteUrl(path), [path]);
  const shareMessage = React.useMemo(() => buildShareMessage(shareUrl), [buildShareMessage, shareUrl]);

  return (
    <>
      <SeoMeta title={seoTitle} description={seoDescription} url={path} />
      <Box
        sx={{
          minHeight: "100%",
          px: { xs: 2, sm: 3 },
          py: { xs: 3, sm: 5 },
          background:
            "linear-gradient(180deg, #f4fbff 0%, #ffffff 42%, #f8f6ef 100%)",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 900,
            mx: "auto",
            p: { xs: 2.5, sm: 4 },
            borderRadius: 4,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "rgba(255,255,255,0.97)",
            boxShadow: "0 24px 60px rgba(18, 44, 67, 0.08)",
          }}
        >
          <Stack spacing={3}>
            <Stack spacing={1.25}>
              {eyebrow ? (
                <Typography
                  variant="overline"
                  sx={{ letterSpacing: "0.12em", color: "text.secondary", fontWeight: 700 }}
                >
                  {eyebrow}
                </Typography>
              ) : null}
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.05,
                  color: "#14324a",
                  fontSize: { xs: "2rem", sm: "2.75rem" },
                }}
              >
                {title}
              </Typography>
              <Typography variant="body1" sx={{ color: "#49657a", fontSize: "1.05rem" }}>
                {description}
              </Typography>
            </Stack>

            {highlights.length ? (
              <Stack spacing={1}>
                {highlights.map((item) => (
                  <Box key={item} sx={{ display: "flex", gap: 1.1, alignItems: "flex-start" }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        mt: "8px",
                        borderRadius: "50%",
                        backgroundColor: "#0f7ca8",
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" sx={{ color: "#31556f" }}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            ) : null}

            {actions.length ? (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} useFlexGap flexWrap="wrap">
                {actions.map((action) => (
                  <Button
                    key={action.label}
                    type="button"
                    href={action.href}
                    onClick={action.onClick}
                    variant={action.variant || "contained"}
                    size="large"
                    sx={{ borderRadius: 999, px: 2.25 }}
                  >
                    {action.label}
                  </Button>
                ))}
              </Stack>
            ) : null}

            {children}

            <PublicShareActions
              description={
                shareDescription ||
                "Use this message when you share the page in WhatsApp, Facebook, text, or email."
              }
              message={shareMessage}
              shareUrl={shareUrl}
            />
          </Stack>
        </Paper>
      </Box>
    </>
  );
}
